import moment from "moment-timezone"

export * from "./consent-extend-holder.helper"
export * from "./consent-share-email.helper"
export * from "./cost-availability.helper"
export * from "./format.helper"
export * from "./meter.helper"
export * from "./parser.helper"
export * from "./source-filter.helper"
export * from "./subscription-plan.helper"
export * from "./task-progress.helper"

export const capitalizeFirstLetter = (str?: string) => {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Permet de vérifier si une valeur est supérieure à une valeur minimale et de retourner un message d'erreur si ce n'est pas le cas
 * @param {Number} value - Valeur à vérifier
 * @param {Number} min - Valeur minimale
 * @param {Object} options - Options de la fonction. Permet de mettre un nom personnalisé au champs, un suffixe et de préciser si le champs est féminin
 * @return {string|true} - Message d'erreur ou true si la valeur est supérieure à la valeur minimale
 * @example
 * // returns 'Ce champ doit être supérieur à 10'
 * minValue(5, 10)
 * // returns 'Le nombre de pièces doit être supérieur à 10'
 * minValue(5, 10, { name: 'Le nombre de pièces' })
 * // returns 'Le nombre de pièces doit être supérieur à 10 m²'
 * minValue(5, 10, { name: 'Le nombre de pièces', suffix: 'm²' })
 * // returns 'Le nombre de fenêtres doit être supérieure à 10 m²'
 * minValue(5, 10, { name: 'Le nombre de fenêtres', suffix: 'm²', female: true })
 */
type MinValueOptions = {
    name?: string
    suffix?: string
    female?: boolean
}

export const minValue = (
    value: number,
    min: number,
    options: MinValueOptions = {}
): string | true => {
    return value < min
        ? `${options.name || "Ce champ"} doit être supérieur${
              options.female ? "e" : ""
          } à ${min}${options.suffix ? " " + options.suffix : ""}`
        : true
}

/**
 * Vérifie si la valeur est négative
 * @param {number | null} value - Valeur à vérifier
 * @return {boolean} - true si la valeur est négative
 */
export const isNegativeValue = (value: number | null | undefined) => {
    return value === undefined || value === null || value < 0
}

export const downloadPDF = (pdf: string, filename: string) => {
    const linkSource = `data:application/pdf;base64,${pdf}`
    const downloadLink = document.createElement("a")
    const fileName = filename

    downloadLink.href = linkSource
    downloadLink.download = (fileName || "document") + ".pdf"
    downloadLink.click()
    return linkSource
}

/**
 * Normalise une chaîne de caractères en supprimant les accents, les caractères spéciaux et en mettant en minuscule
 * @param str - Chaîne de caractères à normaliser
 * @returns Chaîne de caractères normalisée
 */
export function normalizeString(str: string): string {
    return str
        .toLowerCase()
        .normalize("NFD") // Décompose les accents
        .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
        .replace(/[^a-z0-9\s]/g, "") // Supprime les caractères spéciaux
        .replace(/\s+/g, " ") // Supprime les espaces multiples
        .trim()
}

/**
 * Calcule la distance de Levenshtein entre deux chaînes de caractères
 * @param a - Première chaîne de caractères
 * @param b - Deuxième chaîne de caractères
 * @returns Distance de Levenshtein entre les deux chaînes de caractères
 */
function levenshtein(a: string, b: string): number {
    const matrix: number[][] = []

    const lenA = a.length
    const lenB = b.length

    // Initialisation
    for (let i = 0; i <= lenB; i++) {
        matrix[i] = [i]
    }
    for (let j = 0; j <= lenA; j++) {
        matrix[0][j] = j
    }

    // Calcul
    for (let i = 1; i <= lenB; i++) {
        for (let j = 1; j <= lenA; j++) {
            const cost = a[j - 1] === b[i - 1] ? 0 : 1
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1, // suppression
                matrix[i][j - 1] + 1, // insertion
                matrix[i - 1][j - 1] + cost // substitution
            )
        }
    }

    return matrix[lenB][lenA]
}

/**
 * Calcule la similarité entre deux chaînes de caractères
 * @param a - Première chaîne de caractères
 * @param b - Deuxième chaîne de caractères
 * @returns Similarité entre les deux chaînes de caractères (entre 0 et 1)
 */
export function stringSimilarity(a: string, b: string): number {
    const strA = normalizeString(a)
    const strB = normalizeString(b)

    if (!strA || !strB) return 0

    const distance = levenshtein(strA, strB)
    return 1 - distance / Math.max(strA.length, strB.length)
}

/**
 * Vérifie si deux chaînes de caractères sont similaires au-delà d'un certain seuil
 * @param a - Première chaîne de caractères
 * @param b - Deuxième chaîne de caractères
 * @param threshold - Seuil de similarité
 * @returns true si les chaînes sont similaires, false sinon
 */
export function isStringSimilar(
    a: string,
    b: string,
    threshold = 0.8
): boolean {
    return stringSimilarity(a, b) >= threshold
}

/**
 * Trouve dans un objet le sous objet ayant pour clé le paramètre key
 * @param object - objet dans lequel chercher
 * @param key - clé à trouver
 * @return Le sous objet ou undefined si non trouvé
 */
export function findNestedObjectByKey(object: any, key: string): any {
    if (object?.[key]) return object[key]
    for (const prop in object) {
        if (
            Object.prototype.hasOwnProperty.call(object, prop) &&
            typeof object?.[prop] === "object"
        ) {
            const result = findNestedObjectByKey(object?.[prop], key)
            if (result) return result
        }
    }
}

/**
 * Fonction d'aide permettant de compter le nombre de lundi entre 2 dates
 * @param startDate - Date de début
 * @param endDate - Date de fin
 */
export function countMondays(startDate: Date, endDate: Date) {
    const start = moment(startDate).startOf("day")
    const end = moment(endDate).startOf("day")
    let count = 0

    while (start.isSameOrBefore(end)) {
        if (start.isoWeekday() === 1) {
            // Vérifie si c'est un lundi
            count++
        }
        start.add(1, "day") // Passe au jour suivant
    }

    return count
}

/**
 * Generate an unique id based on date now and base 36
 * @return unique id generated
 */
export function createUniqueId(): string {
    return (Math.random().toString(36) + Date.now().toString(36)).substr(2)
}

/**
 * Sérialise les paramètres pour une requête HTTP dans une chaîne de requête
 * Filtre les valeurs nulles ou undefined
 * @param params - Objet des paramètres
 * @returns Chaîne de requête sérialisée
 */
export function paramsSerializer(params: Record<string, any>): string {
    return Object.entries(params)
        .filter(([, value]) => value != null)
        .map(([key, value]) =>
            Array.isArray(value)
                ? value.map((v) => `${key}=${encodeURIComponent(v)}`).join("&")
                : `${key}=${encodeURIComponent(value as string)}`
        )
        .join("&")
}

/**
 * Valide un numéro de téléphone français
 * @param value - Numéro de téléphone à valider
 * @returns True si le numéro est valide, false sinon
 */
export function phoneValidator(value: string | null | undefined): boolean {
    if (!value) return false
    // Supprime les espaces et valide le format français (0X XX XX XX XX ou +33 X XX XX XX XX)
    return /^(\+33|0)[1-9](\d{2}){4}$/.test(value.replace(/\s/g, ""))
}

/**
 * Met en majuscule la première lettre d'une chaîne
 */
export function capitalize(str: string): string {
    if (!str) return ""
    return str
        .toLowerCase()
        .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase())
}
